import React, {
  useState,
} from "react";

import {
  Link,
  useNavigate,
} from "react-router-dom";

import {
  useAuth,
} from "@/context/AuthContext";

import {
  errMsg,
} from "@/lib/api";

import {
  Logo,
  QrewHrLogo,
} from "@/components/Logo";

import {
  Loader2,
  Eye,
  EyeOff,
  LockKeyhole,
  Mail,
  ShieldCheck,
  Users,
  Calculator,
  ReceiptText,
  BarChart3,
  CheckCircle2,
} from "lucide-react";


const FEATURES = [
  {
    icon: Users,
    title: "Employee & Client Masters",
    text: "Centralized workforce and deployment records.",
  },
  {
    icon: Calculator,
    title: "Automated Payroll",
    text: "Wages, PF, ESI, PT and employer contributions.",
  },
  {
    icon: ReceiptText,
    title: "Billing & Invoices",
    text: "Payroll-linked service charge and GST billing.",
  },
  {
    icon: BarChart3,
    title: "Payroll Intelligence",
    text: "Live operational and financial analytics.",
  },
];


export default function Login() {
  const {
    login,
  } = useAuth();

  const navigate =
    useNavigate();

  const [
    email,
    setEmail,
  ] = useState("");

  const [
    password,
    setPassword,
  ] = useState("");

  const [
    showPassword,
    setShowPassword,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    loading,
    setLoading,
  ] = useState(false);


  const submit = async (e) => {
    e.preventDefault();

    setError("");
    setLoading(true);

    try {
      await login(
        email,
        password
      );

      navigate(
        "/dashboard"
      );

    } catch (err) {
      setError(
        errMsg(err)
      );

    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="nk-login-page">

      {/* =====================================================
          BRAND / PRODUCT PANEL
      ====================================================== */}

      <section className="nk-login-brand">

        <div className="nk-login-brand-glow nk-login-glow-one" />
        <div className="nk-login-brand-glow nk-login-glow-two" />


        <div className="nk-login-brand-top">

          <div className="nk-login-logo-shell">
            <Logo height={38} />
          </div>

          <div className="nk-login-secure">
            <ShieldCheck size={15} />
            Secure Payroll Platform
          </div>

        </div>


        <div className="nk-login-brand-main">

          <div className="nk-login-eyebrow">
            Nakshatra Housekeeping Services
          </div>

          <h1>
            Payroll management,
            <span>
              simplified for operations.
            </span>
          </h1>

          <p>
            Manage employees, attendance, payroll,
            statutory compliance, billing and
            client collections from one secure system.
          </p>


          <div className="nk-login-feature-grid">

            {FEATURES.map(
              ({
                icon: Icon,
                title,
                text,
              }) => (
                <div
                  key={title}
                  className="nk-login-feature"
                >

                  <div className="nk-login-feature-icon">
                    <Icon size={17} />
                  </div>

                  <div>
                    <strong>
                      {title}
                    </strong>

                    <span>
                      {text}
                    </span>
                  </div>

                </div>
              )
            )}

          </div>


          <div className="nk-login-flow">

            <div>
              <CheckCircle2 size={15} />
              Employee Master
            </div>

            <span>→</span>

            <div>
              <CheckCircle2 size={15} />
              Attendance
            </div>

            <span>→</span>

            <div>
              <CheckCircle2 size={15} />
              Payroll
            </div>

            <span>→</span>

            <div>
              <CheckCircle2 size={15} />
              Invoice
            </div>

          </div>

        </div>


        <div className="nk-login-brand-footer">
          Nakshatra Payroll Management System
        </div>

      </section>


      {/* =====================================================
          SIGN-IN PANEL
      ====================================================== */}

      <section className="nk-login-form-side">

        <div className="nk-login-mobile-logo">
          <Logo onLight height={39} />
        </div>


        <div className="nk-login-form-card">

          <div className="nk-login-form-badge">
            <LockKeyhole size={15} />
            Authorized Access
          </div>


          <h2>
            Welcome back
          </h2>

          <p className="nk-login-form-subtitle">
            Sign in to access your payroll workspace.
          </p>


          <form
            onSubmit={submit}
            className="nk-login-form"
          >

            <div>

              <label>
                Email Address
              </label>

              <div className="nk-login-input-wrap">

                <Mail
                  size={17}
                  className="nk-login-input-icon"
                />

                <input
                  data-testid="login-email"
                  type="email"
                  required
                  autoComplete="username"
                  value={email}
                  onChange={(e) =>
                    setEmail(
                      e.target.value
                    )
                  }
                  placeholder="admin@nakshatrahousekeeping.com"
                />

              </div>

            </div>


            <div>

              <div className="nk-login-label-row">

                <label>
                  Password
                </label>

                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>

              </div>


              <div className="nk-login-input-wrap">

                <LockKeyhole
                  size={17}
                  className="nk-login-input-icon"
                />

                <input
                  data-testid="login-password"
                  type={
                    showPassword
                      ? "text"
                      : "password"
                  }
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) =>
                    setPassword(
                      e.target.value
                    )
                  }
                  placeholder="Enter your password"
                />


                <button
                  type="button"
                  className="nk-login-password-toggle"
                  onClick={() =>
                    setShowPassword(
                      (v) => !v
                    )
                  }
                  aria-label={
                    showPassword
                      ? "Hide password"
                      : "Show password"
                  }
                >
                  {showPassword ? (
                    <EyeOff size={17} />
                  ) : (
                    <Eye size={17} />
                  )}
                </button>

              </div>

            </div>


            {error && (
              <div
                data-testid="login-error"
                className="nk-login-error"
              >
                {error}
              </div>
            )}


            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="nk-login-submit"
            >

              {loading && (
                <Loader2
                  size={17}
                  className="animate-spin"
                />
              )}

              <span>
                {loading
                  ? "Signing in..."
                  : "Sign in to Payroll"}
              </span>

            </button>

          </form>


          <div className="nk-login-security-note">

            <ShieldCheck size={15} />

            <span>
              Your session is protected and access is role-based.
            </span>

          </div>


          <div className="nk-login-developed">

            <span>
              Customized &amp; Developed by
            </span>

            <span className="nk-login-qrew">
              <QrewHrLogo height={15} />
            </span>

          </div>

        </div>

      </section>

    </div>
  );
}
